// The round's country, clock and money on this phone (Step 1, 29 Sep 2026;
// docs/plans/step1-country/PLAN.md). Set once at start-up from /api/me, before any screen
// is drawn, and never changed afterwards: if another account signs in, the page either
// reloads for it or keeps the first account's screens covered, and those screens keep the
// first account's settings. Every round is the UK, London time and pounds for now, which is
// also what everything assumes before the answer arrives. Imports nothing, so the
// foundation helpers (ui.ts) can read it without pulling in the rest of the app.

export interface RoundConfig { country: 'GB' | 'US' | 'CA'; timeZone: string; currency: 'GBP' | 'USD' | 'CAD' }

const UK: RoundConfig = { country: 'GB', timeZone: 'Europe/London', currency: 'GBP' }
let config: RoundConfig = UK
let set = false

function knownZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !zone) return false
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

// From /api/me's `round`. Only the first call counts (see above); anything missing or not
// understood leaves the UK.
export function setRoundConfig(round: Partial<RoundConfig> | null | undefined): void {
  if (set) return
  set = true
  if (!round) return
  const country = round.country === 'US' || round.country === 'CA' ? round.country : 'GB'
  const currency = country === 'US' ? 'USD' : country === 'CA' ? 'CAD' : 'GBP'
  config = { country, timeZone: knownZone(round.timeZone) ? round.timeZone : UK.timeZone, currency }
}

export function roundConfig(): RoundConfig {
  return config
}

// The server's way in (Send all's words, functions/lib/chase-send.ts): one round's settings for the length of one
// synchronous call, then back as they were. Synchronous only, so no other request can see them.
export function withRoundConfig<T>(round: Partial<RoundConfig>, fn: () => T): T {
  const was = config, wasSet = set
  const country = round.country === 'US' || round.country === 'CA' ? round.country : 'GB'
  config = { country, timeZone: knownZone(round.timeZone) ? round.timeZone : UK.timeZone, currency: country === 'US' ? 'USD' : country === 'CA' ? 'CAD' : 'GBP' }
  set = true
  try { return fn() } finally { config = was; set = wasSet }
}

// For the checks only: start again as a fresh page would.
export function resetRoundConfigForTests(round?: Partial<RoundConfig>): void {
  config = UK
  set = false
  if (round) setRoundConfig(round)
}

const dayFormats = new Map<string, Intl.DateTimeFormat>()
const clockFormats = new Map<string, Intl.DateTimeFormat>()

// yyyy-mm-dd in a time zone, put together from its parts. Not en-CA's own format: iPhone software 17.0 writes it
// "10/21/2026" (seen on the simulator, 21 Oct 2026), and Today then could not load at all ("from must be a real date").
export function dayIn(zone: string, now: Date = new Date()): string {
  let f = dayFormats.get(zone)
  if (!f) { f = new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }); dayFormats.set(zone, f) }
  const parts = f.formatToParts(now)
  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

// yyyy-mm-dd on the round's clock.
export function roundDay(now: Date = new Date()): string {
  return dayIn(config.timeZone, now)
}

// "14:05" on the round's clock.
export function roundClock(now: Date): string {
  const zone = config.timeZone
  let f = clockFormats.get(zone)
  if (!f) { f = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); clockFormats.set(zone, f) }
  const parts = f.formatToParts(now)
  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00'
  return `${part('hour')}:${part('minute')}`
}

// The moment a round's clock reads "HH:MM" on its day "yyyy-mm-dd" (a time typed into Change times, 0083). Worked out
// from the clock itself, so a clock change that day is handled: a time the clocks skip lands an hour on, one they repeat
// is the second.
export function roundMoment(day: string, hhmm: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const t = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!d || !t || Number(t[1]) > 23 || Number(t[2]) > 59) return null
  const wanted = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]))
  let guess = wanted
  const tried: number[] = []
  for (let i = 0; i < 3; i++) {
    const onDay = roundDay(new Date(guess))
    const clock = roundClock(new Date(guess))
    const read = Date.UTC(Number(onDay.slice(0, 4)), Number(onDay.slice(5, 7)) - 1, Number(onDay.slice(8, 10)), Number(clock.slice(0, 2)), Number(clock.slice(3, 5)))
    if (read === wanted) return new Date(guess)
    tried.push(guess)
    guess += wanted - read
  }
  // Never read on that clock (the hour the clocks skip): the later of the two it swung between, an hour on.
  return new Date(Math.max(...tried))
}

export function currencySymbol(): '£' | '$' {
  return config.currency === 'GBP' ? '£' : '$'
}

// Price text uses the round's currency code and Intl's marks, including narrow symbols.
// Unicode currency signs also cover old notes written before a round changed settings.
const priceMaskPatterns=new Map<string,[RegExp,RegExp]>()
export function maskPriceAmounts(text:string,currency:string=config.currency):string {
  let patterns=priceMaskPatterns.get(currency)
  if(!patterns){
    const escape=(s:string)=>s.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')
    const tokens=new Set([currency])
    for(const display of ['symbol','narrowSymbol'] as const){
      for(const p of new Intl.NumberFormat('en-GB',{style:'currency',currency,currencyDisplay:display}).formatToParts(0))if(p.type==='currency')tokens.add(p.value)
    }
    const marks=[...tokens].sort((a,b)=>b.length-a.length).map(t=>/\p{L}/u.test(t)?`(?<![\\p{L}_])${escape(t)}(?![\\p{L}_])`:escape(t)).join('|')
    const mark=`(?:${marks}|\\p{Sc})`,amount='[-+]?(?:\\d{1,3}(?:[,\\u00a0\\u202f]\\d{3})+|\\d+)(?:[.,]\\d{1,2})?'
    patterns=[new RegExp(`(${mark}\\s*)${amount}`,'giu'),new RegExp(`${amount}(\\s*${mark})`,'giu')]
    priceMaskPatterns.set(currency,patterns)
  }
  return text.replace(patterns[0],'$1…').replace(patterns[1],'…$1')
}

// 'pounds' or 'dollars': box labels and help text ("Cash taken, in dollars").
export function currencyWord(): 'pounds' | 'dollars' {
  return config.currency === 'GBP' ? 'pounds' : 'dollars'
}

// The small unit's mark, for wording like "72p in every £1" (a US or Canadian round: "72¢ in every $1"; Step 2).
export function minorUnit(): 'p' | '¢' {
  return config.currency === 'GBP' ? 'p' : '¢'
}

// Days and times in customers' messages, the American way for a US or Canadian round (Liam, 10 Oct 2026: "Dates yes"):
// "Tuesday, October 13" and "2:30 PM" where a UK round writes "Tuesday 13th October" and "14:30" (Step 2 round 4).
export function monthFirst(): boolean {
  return config.country !== 'GB'
}

// "14:30" -> "2:30pm", "09:00-11:00" -> "9am-11am" in a UK round (Liam, 11 Oct 2026: "you decide"; built 12 Oct);
// "2:30 PM", "9:00 AM-11:00 AM" in a US or Canadian round. Only the time picker's own two-digit 24-hour times are
// turned: a time typed by hand ("1:30-3:00", "around 2:30", "10am") says what the owner meant and is left as typed.
export function messageClock(text: string): string {
  if (/\b[ap]\.?m\b|\d[ap]m\b/i.test(text)) return text
  return text.replace(/(^|[^\d:])([01]\d|2[0-3]):([0-5]\d)(?![\d:])/g, (_m, before: string, h: string, mm: string) => `${before}${clockWords(Number(h), mm)}`)
}
export function clock12(hour: number, mm: string): string {
  return `${hour % 12 === 0 ? 12 : hour % 12}:${mm} ${hour < 12 ? 'AM' : 'PM'}`
}
// "9am", "2:30pm", "12pm": the way a UK round writes a time.
export function clockUK(hour: number, mm: string): string {
  return `${hour % 12 === 0 ? 12 : hour % 12}${mm === '00' ? '' : `:${mm}`}${hour < 12 ? 'am' : 'pm'}`
}
// An hour and minutes ("09", "30" or 9, "30") as the round writes a time.
export function clockWords(hour: number, mm: string): string {
  return monthFirst() ? clock12(hour, mm) : clockUK(hour, mm)
}

// Dates and times on the app's own screens, the round's way too (Step 2 round 7; Liam, 11 Oct 2026: "Yes"): a US or
// Canadian round reads "Sun, Oct 11", "Oct 11" and "2:30 PM" where a UK round reads "Sun 11 Oct", "11 Oct" and
// "2:30pm". Stored values never change: only what a screen writes.
// A saved time or time range as picked ("14:30", "09:00-11:30"); one typed by hand is shown as typed (messageClock).
export function shownTime(text: string): string {
  return messageClock(text)
}
// Now on the round's clock, as a screen writes it.
export function shownClock(now: Date): string {
  const t = roundClock(now)
  return clockWords(Number(t.slice(0, 2)), t.slice(3))
}
// "11 Oct" / "Oct 11"; with a year "11 Oct 2025" / "Oct 11, 2025". The month is given as the caller writes it.
export function dayMonth(day: number | string, month: string, year?: number | string): string {
  const y = year === undefined || year === '' ? '' : String(year)
  if (!monthFirst()) return y ? `${day} ${month} ${y}` : `${day} ${month}`
  return y ? `${month} ${day}, ${y}` : `${month} ${day}`
}
// The words a screen's own date formatting uses: 'en-US' ("Sun, Oct 11", "Sunday, October 11") for a US or Canadian
// round, 'en-GB' for a UK one, as before.
export function screenLocale(): 'en-GB' | 'en-US' {
  return monthFirst() ? 'en-US' : 'en-GB'
}
// A moment on the phone's own clock ("04:34" / "4:34 AM"), as Chats and calls write it.
export function phoneClock(d: Date): string {
  return clockWords(d.getHours(), String(d.getMinutes()).padStart(2, '0'))
}
// "Sun 11 Oct" / "Sun, Oct 11"; with a year "Sun 11 Oct 2025" / "Sun, Oct 11, 2025".
export function weekdayDayMonth(weekday: string, day: number | string, month: string, year?: number | string): string {
  return monthFirst() ? `${weekday}, ${dayMonth(day, month, year)}` : `${weekday} ${dayMonth(day, month, year)}`
}

// "Postcode" in the app's own words, as the round's country says it: "ZIP code" in the US, "postal code" in Canada
// (Step 2 round 3). A UK round's words are untouched; stored data and field names stay "postcode".
export function postcodeWords(text: string): string {
  if (config.country === 'GB') return text
  return text.replace(/\b([Pp])ostcode(s?)\b/g, (_m, p: string, plural: string) => (config.country === 'US' ? 'ZIP code' : p === 'P' ? 'Postal code' : 'postal code') + plural)
}

// The number a phone in this round dials the country with: 44 for the UK, 1 for the US and Canada.
export function callingCode(): '44' | '1' {
  return config.country === 'GB' ? '44' : '1'
}

// How often, counted forward (Step 2 round 2, 10 Oct 2026). A US or Canadian round has "monthly": stored as 30 days and
// counted in calendar months, so a customer cleaned on the 1st is due on the 1st (the 31st falls back to a shorter
// month's last day). Every other cycle, and every cycle in a UK round, counts days exactly as before.
// functions/lib/cycle.ts is the server's copy; the us-dates check keeps the two the same.
export const MONTHLY_DAYS = 30

export function monthlyCycles(): boolean {
  return config.country !== 'GB'
}

// A patio, roof and driveway round comes round every few years (0073, Liam picked A, 15 Oct 2026): its How often also
// offers 2, 3 and 5 years. Set from /api/me (me.ts) whenever it is read, as the trade can be chosen after start-up.
let yearsRound = false
export function setYearsRound(on: boolean): void { yearsRound = on }
export function yearCycles(): boolean { return yearsRound }

function plusDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

function plusMonths(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  const zero = y * 12 + (m - 1) + n
  const ty = Math.floor(zero / 12)
  const tm = zero % 12
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate()
  return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

// k cycles on from a day.
export function cyclesOn(iso: string, days: number, k = 1): string {
  return monthlyCycles() && days === MONTHLY_DAYS ? plusMonths(iso, k) : plusDays(iso, days * k)
}

// One cycle on, then whole cycles until it is today or later (as functions/lib/autobook.ts books after a Done).
export function nextCycleOnOrAfter(from: string, days: number, today: string): string {
  let k = 1
  let next = cyclesOn(from, days, k)
  while (k < 601 && next < today) next = cyclesOn(from, days, ++k)
  return next
}

// The UK's own zone names (the phone says one of these in the UK).
export const UK_ZONES = new Set(['Europe/London', 'Europe/Belfast', 'GB', 'GB-Eire'])

// A US zone the phone may report ("America/Detroit", "America/Indiana/Indianapolis", "US/Eastern") as the one of the
// seven offered that keeps the same clock all year (the same hours in January and July); null for a zone outside the US.
// Older names some phones still report (Safari says "America/Indianapolis") are in it too.
const US_EXTRA = new Set(['America/Detroit', 'America/Menominee', 'America/Boise', 'America/Juneau', 'America/Sitka', 'America/Metlakatla', 'America/Yakutat', 'America/Nome', 'America/Adak',
  'America/Indianapolis', 'America/Louisville', 'America/Fort_Wayne', 'America/Knox_IN', 'America/Atka', 'America/Shiprock', 'Navajo'])
export function usZoneFor(zone: string, offered: string[]): string | null {
  if (!zone) return null
  if (offered.includes(zone)) return zone
  const american = US_EXTRA.has(zone) || /^America\/(Indiana|Kentucky|North_Dakota)\//.test(zone) || zone.startsWith('US/')
  if (!american) return null
  const offset = (z: string, at: Date): number | null => {
    try {
      const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: z, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })
        .formatToParts(at).map((x) => [x.type, x.value]))
      return Math.round((Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute)) - at.getTime()) / 60000)
    } catch { return null }
  }
  const jan = new Date(Date.UTC(2026, 0, 15, 12)), jul = new Date(Date.UTC(2026, 6, 15, 12))
  const mine = [offset(zone, jan), offset(zone, jul)]
  if (mine[0] === null || mine[1] === null) return null
  return offered.find((z) => offset(z, jan) === mine[0] && offset(z, jul) === mine[1]) ?? null
}
